import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent, DiscountUpdateErrorEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCodeCustom - The Custom discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorDiscountCodeCustom', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /**
   * Code -> custom error message map, parsed from the `data-discount-config`
   * attribute injected by snippets/cart-discount.liquid from
   * `shop.metaobjects.discount_configuration`. Keeps the map in the theme
   * merchant-configurable instead of hardcoded in this file.
   *
   * @type {Record<string, string>}
   */
  #discountConfig = {};


  #createAbortController () {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  connectedCallback () {
    super.connectedCallback()
    this.#discountConfig = this.#parseDiscountConfig();
    document.addEventListener(ThemeEvents.discountApply, this.onDiscountApply)
    document.addEventListener(ThemeEvents.discountRemove, this.onDiscountRemove)
  }

  disconnectedCallback () {
    document.removeEventListener(ThemeEvents.discountApply, this.onDiscountApply)
    document.removeEventListener(ThemeEvents.discountRemove, this.onDiscountRemove)
    super.disconnectedCallback()
  }

  onDiscountApply = (evt) => {
    this.applyDiscountCode(evt.detail.code)
  }

  onDiscountRemove = (evt) => {
    this.removeDiscountCode(evt.detail.code)
  }

  onInputKeydown = (evt) => {
    if (evt.key !== 'Enter') return
    evt.preventDefault()
    evt.stopPropagation()
    this.applyDiscount(evt)
  }

  /**
   * Click on the Apply button — declared via `on:click="/onApplyClick"` on
   * the button in cart-discount.liquid.
   */
  onApplyClick = (evt) => {
    evt.preventDefault()
    evt.stopPropagation()
    this.applyDiscount(evt)
  }

  applyDiscount = async (event) => {
    if (typeof this.dataset.sectionId !== 'string') return;
    const input =
      (event?.target instanceof HTMLInputElement && event.target) ||
      this.querySelector('input[id="cart-discount"]');
    if (!(input instanceof HTMLInputElement)) return;
    if (!input.value) return
    this.applyDiscountCode(input.value, event);
  };

  applyDiscountCode = async (discountCodeValue, event = null) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorDiscountCodeCustom, cartDiscountErrorShipping } = this.refs;
    const abortController = this.#createAbortController();
    // Used by the error branches below to clear the input.
    const discountCode = this.querySelector('input[id="cart-discount"]');

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorDiscountCodeCustom.classList.add('hidden')
      cartDiscountErrorShipping.classList.add('hidden');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: discountCodeValue,
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code', discountCodeValue);
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping', discountCodeValue);
          discountCode.value = '';
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      this.refresh(data)
    } catch (error) {
      console.error("apply discount error:", error)
    } finally {
      this.#activeFetch = null;
      if (event) cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  }

  refresh = async (data) => {
    const cartDrawer = document.querySelector("cart-drawer")
    if (cartDrawer) cartDrawer.renderContents(data, true)
    else {
      const newHtml = data.sections[this.dataset.sectionId];
      this.getSectionsToRender().forEach((section) => {
        const sectionEl = document.getElementById(section.id);
        if (!sectionEl) return;

        const el = sectionEl.querySelector(section.selector) || sectionEl;
        el.innerHTML = CartDiscount.getElementHTML(newHtml, section.selector);
        // el.innerHTML = newHtml.querySelector(section.selector)
      });
    }
  }

  getSectionsToRender () {
    return [{
      id: 'cart-items',
      section: this.dataset.sectionId,
      selector: 'cart-items'
    },
    {
      id: 'cart-summary',
      section: document.getElementById('cart-summary').dataset.section,
      selector: '.cart__summary'
    }]
  }

  /**
    * Gets the innerHTML of an element.
    * @param {string} html - Section HTML.
    * @param {string} selector - CSS selector for the element to get the innerHTML of.
    * @returns {string}
    */
  static getElementHTML (html, selector) {
    const tmpl = document.createElement('template');
    tmpl.innerHTML = html;

    const el = tmpl.content.querySelector(selector);
    return el ? el.innerHTML : '';
  }


  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;
    this.removeDiscountCode(discountCode)

  };

  removeDiscountCode = async (discountCode) => {
    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      this.refresh(data)
    } catch (error) {
      console.error("remove discount error:", error)
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handles the discount error.
   *
   * Priority:
   *   1. If the code is in `#discountConfig` (configured via metaobject),
   *      show that message in the custom error slot.
   *   2. Otherwise, fall back to the default discount-code / shipping slot.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError (type, code = '') {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorDiscountCodeCustom, cartDiscountErrorShipping } = this.refs;
    const upperCode = (code || '').toUpperCase();
    const customMessage = this.#discountConfig[upperCode];

    let target;
    if (customMessage) {
      target = cartDiscountErrorDiscountCodeCustom;
      // textContent avoids any HTML injection from the metaobject value.
      target.textContent = customMessage;
    } else {
      target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    }

    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
    document.dispatchEvent(new DiscountUpdateErrorEvent(type, code));
    const couponsModal = document.querySelector("coupons-modal.is-open");
    if (couponsModal) {
      couponsModal.close();
    }
  }

  /**
   * Parses the `data-discount-config` JSON injected by snippets/cart-discount.liquid.
   * Returns an empty object on any parse/format failure so the component still
   * works (just without custom messages).
   *
   * @returns {Record<string, string>}
   */
  #parseDiscountConfig () {
    const raw = this.dataset.discountConfig;
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        /** @type {Record<string, string>} */
        const out = {};
        for (const [key, value] of Object.entries(parsed)) {
          if (typeof value === 'string' && value.length > 0) {
            // Normalize keys to upper-case so the runtime lookup in
            // #handleDiscountError is case-insensitive regardless of how the
            // merchant entered the code in the metaobject.
            out[key.toUpperCase()] = value;
          }
        }
        return out;
      }
    } catch (err) {
      console.warn('[cart-discount] failed to parse data-discount-config:', err);
    }
    return {};
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts () {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}

/* global trapFocus, removeTrapFocus, customElements, HTMLElement */

if (!customElements.get('coupons-modal')) {
  class CouponsModal extends HTMLElement {
    constructor() {
      super();

      this.context = this.dataset.context || 'drawer';
      this.panel = this.querySelector('.coupons-modal__panel');
      this.backdrop = this.querySelector('.coupons-modal__backdrop');
      this.errorEl = this.querySelector('.js-coupons-error');

      this.keyupHandler = (e) => e.key === 'Escape' && this.close();
      this.handleClick = this.handleClick.bind(this);
      this.handleTriggerClick = this.handleTriggerClick.bind(this);
    }

    connectedCallback () {
      this.addEventListener('click', this.handleClick);

      this.couponsList = this.querySelector('coupons-list');

      this.triggers = Array.from(this.parentElement.querySelectorAll('.js-open-coupons'));
      this.triggers.forEach((trigger) => {
        trigger.addEventListener('click', this.handleTriggerClick);
      });

      Array.from(document.querySelectorAll('coupons-modal'))
        .filter((el) => el !== this && el.dataset.context === this.context && document.body.contains(el))
        .forEach((el) => el.remove());

      this.originalParent = this.parentNode;
      this.originalNextSibling = this.nextSibling;
    }

    disconnectedCallback () {
      if (this.triggers) {
        this.triggers.forEach((trigger) => {
          trigger.removeEventListener('click', this.handleTriggerClick);
        });
      }
    }

    handleTriggerClick (evt) {
      evt.preventDefault();
      evt.stopPropagation();
      this.open(evt.currentTarget);
    }

    open (opener) {
      this.opener = opener;

      if (this.originalParent && this.parentNode !== document.body) {
        document.body.appendChild(this);
      }

      this.hidden = false;
      this.setAttribute('aria-hidden', 'false');
      this.offsetHeight;
      this.classList.add('is-open');

      trapFocus(this, this.querySelector('.coupons-modal__close'));

      document.addEventListener('keyup', this.keyupHandler);
    }

    close () {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      removeTrapFocus(this.opener);
      document.removeEventListener('keyup', this.keyupHandler);

      const onEnd = (e) => {
        if (e.target !== this.panel) return;
        this.hidden = true;
        this.restoreToOrigin();
        this.panel.removeEventListener('transitionend', onEnd);
      };
      this.panel.addEventListener('transitionend', onEnd);
      setTimeout(() => {
        if (!this.classList.contains('is-open')) {
          this.hidden = true;
          this.restoreToOrigin();
        }
      }, 400);
    }

    restoreToOrigin () {
      if (this.parentNode === document.body && this.originalParent && document.body.contains(this.originalParent)) {
        this.originalParent.insertBefore(this, this.originalNextSibling);
      }
    }

    handleClick (evt) {
      if (evt.target.closest('.js-close-coupons')) {
        this.close();
      }
    }
  }

  customElements.define('coupons-modal', CouponsModal);
}


/* global document */

async function morphSectionSafe (sectionId, html) {
  const drawer = document.getElementById('cart-drawer');
  const wasOpen = drawer ? drawer.hasAttribute('open') : false;

  let savedAriaHidden = null;
  let savedBodyTop = '';
  let savedBodyFixed = false;
  let savedHtmlHeight = '';
  let savedOverlayVisible = false;

  if (wasOpen && drawer) {
    savedAriaHidden = drawer.getAttribute('aria-hidden');
    savedBodyTop = document.body.style.top;
    savedBodyFixed = document.body.classList.contains('fixed');
    savedHtmlHeight = document.documentElement.style.height;
    const overlay = document.querySelector('.js-overlay');
    savedOverlayVisible = !!(overlay && overlay.classList.contains('is-visible'));
  }

  await morphSection(sectionId, html);

  if (wasOpen && drawer) {
    drawer.setAttribute('open', '');
    drawer.setAttribute('aria-hidden', savedAriaHidden || 'false');
    document.body.classList.toggle('fixed', savedBodyFixed);
    if (savedBodyTop) document.body.style.top = savedBodyTop;
    document.documentElement.style.height = savedHtmlHeight || '100vh';
    if (savedOverlayVisible) {
      const overlay = document.querySelector('.js-overlay');
      if (overlay) overlay.classList.add('is-visible');
    }
  }
}
